import { createContext, useContext } from 'react';

import {
  TRunIngestionByTableProps,
  useDSIngestionActions,
} from 'Modules/Studio/components/StudioInfo/Ingestion/hooks/useDSIngestionActions.query';
import { useDSIngestionStatus } from 'Modules/Studio/components/StudioInfo/Ingestion/hooks/useDSIngestionStatus.query';
import { useFullIngestionStatus } from 'Modules/Studio/components/StudioInfo/Ingestion/hooks/useFullIngestionStatus.query';

export type IngestionByDataSourceContextType = {
  runIngestionByDS: (dataSourceId: number) => void;
  runByTable: ({ dataSourceId, tableName }: TRunIngestionByTableProps) => void;
  stopIngestion: (ingestionId: string) => void;
  isLoadingFullIngestionStatus: boolean;
  isFullIngestionActive: boolean;
  isStopping: boolean;
};

/**
 * IngestionByDataSourceContext & IngestionByDataSourceProvider
 * Used to wrap the useIngestionByDataSource hook
 * To avoid prop drilling from HOC components down to the children
 */
const IngestionByDataSourceContext = createContext<IngestionByDataSourceContextType | undefined>(
  undefined
);

function IngestionByDataSourceProvider({ children }: { children: React.ReactNode }) {
  const { runByDS, stopByDS, runByTable } = useDSIngestionActions();
  const { isPending } = useDSIngestionStatus();
  const { isRunning, isStopping } = useFullIngestionStatus();

  return (
    <IngestionByDataSourceContext.Provider
      value={{
        runIngestionByDS: runByDS,
        stopIngestion: stopByDS,
        runByTable,
        isLoadingFullIngestionStatus: isPending,
        isFullIngestionActive: isRunning || isStopping,
        isStopping,
      }}
    >
      {children}
    </IngestionByDataSourceContext.Provider>
  );
}

function useIngestionByDataSourceContext() {
  const context = useContext(IngestionByDataSourceContext);

  if (context === undefined) {
    throw new Error(
      'IngestionByDataSourceContext must be used within a IngestionByDataSourceProvider'
    );
  }
  return context;
}

export { IngestionByDataSourceProvider, useIngestionByDataSourceContext };
